/** AR QA capture kernel. Pure: HUD clock, 2 Hz buffer, marks, batches.
 *  Does not touch pose_filter. `still` is speed < QA_STILL_MPS (the
 *  pose_filter sigma branch is not read — that would couple the filter). */

export const QA_HZ = 2
export const QA_MAX_SAMPLES = 3600
export const QA_BATCH_MS = 10_000
export const QA_SHOT_MS = 500
export const QA_SHOT_QUEUE = 12
export const QA_SHOT_AUTO_MAX_EDGE = 720

/** Sortable Garage object name: UTC timestamp + zero-padded elapsed ms. */
export function shotFileName(t, iso) {
  const pad = String(Math.max(0, Math.floor(Number(t) || 0))).padStart(7, "0")
  const d = iso ? new Date(iso) : new Date()
  const stamp = Number.isNaN(d.getTime())
    ? "t"
    : d.toISOString().replace(/[-:]/g, "").replace(".", "")
  return `${stamp}_t${pad}.jpg`
}

export const QA_SHOT_HUD_IDS = [
  "ar-qa-clock",
  "ar-qa-wall",
  "ar-qa-hud",
  "ar-hud-compass",
  "ar-hud-gps",
  "ar-hud-nearest",
  "ar-hud-hint",
  "ar-range-text",
  "ar-zone-chip",
  "ar-lock-kicker",
  "ar-lock-title",
  "ar-lock-body",
]

/** Map a CSS box onto the capture canvas. */
export function createShotQueue({post, max = QA_SHOT_QUEUE} = {}) {
  let q = []
  let pumping = false

  function enqueue(item) {
    if (!item) return
    q.push(item)
    while (q.length > max) q.shift()
    pump()
  }

  function pump() {
    if (pumping) return
    pumping = true
    const step = () => {
      const item = q.shift()
      if (!item) {
        pumping = false
        return
      }
      Promise.resolve(post(item)).catch(() => {}).finally(step)
    }
    step()
  }

  return {
    enqueue,
    pending: () => q.length,
    pumping: () => pumping,
  }
}

export function canvasHudBox(elRect, stageRect, canvasW, canvasH) {
  const sw = stageRect.width || 1
  const sh = stageRect.height || 1
  const sx = canvasW / sw
  const sy = canvasH / sh
  return {
    x: (elRect.left - stageRect.left) * sx,
    y: (elRect.top - stageRect.top) * sy,
    w: elRect.width * sx,
    h: elRect.height * sy,
    sx,
    sy,
  }
}
export const QA_STILL_MPS = 0.45
export const QA_MARKS = ["drift", "no_play", "wrong_place", "lock", "ok"]
export const QA_SCHEMA_V = 1

export function qaMarkOk(kind) {
  return QA_MARKS.includes(kind)
}

export function formatElapsed(ms) {
  const t = Math.max(0, Number(ms) || 0)
  const m = Math.floor(t / 60_000)
  const s = Math.floor((t % 60_000) / 1000)
  const d = Math.floor((t % 1000) / 100)
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${d}`
}

/** Wall clock for VLM readout. UTC, space-separated, no T, tenth-second. */
export function formatWall(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return "---- -- -- --:--:--.-"
  const p = (n, w = 2) => String(n).padStart(w, "0")
  const tenth = Math.floor(d.getUTCMilliseconds() / 100)
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}.${tenth}`
}

export function overlayBlock({speedLocked, redacted, hasFix, sampleOk}) {
  if (speedLocked) return "speed"
  if (redacted) return "redact"
  if (!hasFix) return "no_fix"
  if (!sampleOk) return "fix"
  return null
}

export function stillFromSpeed(speedMps) {
  const s = Number(speedMps)
  if (!Number.isFinite(s)) return true
  return s < QA_STILL_MPS
}

export function formatHud(state) {
  const elapsed = formatElapsed(state.elapsedMs)
  const wall = formatWall(state.now)
  const acc = Number.isFinite(state.acc) ? Math.round(state.acc) : "—"
  const live = state.live ? "live" : "nolive"
  const age =
    Number.isFinite(state.ageMs) && state.ageMs < 1e8
      ? `age${(state.ageMs / 1000).toFixed(1)}s`
      : "age—"
  const ok = state.overlayOk ? "ok" : state.overlayBlock || "off"
  const zone = state.zone || "?"
  const d = Number.isFinite(state.dM) ? `${Math.round(state.dM)}m` : "—m"
  const hdg = Number.isFinite(state.hdg) ? Math.round(state.hdg) : "—"
  const hdgAcc = Number.isFinite(state.hdgAcc) ? Math.round(state.hdgAcc) : "—"
  const spd = Number.isFinite(state.spd) ? state.spd.toFixed(1) : "—"
  const still = state.still ? "still" : "move"
  const vid = state.vid || "none"
  const size = Number.isFinite(state.sizeM) ? Math.round(state.sizeM) : "—"
  let cam = ""
  if (state.camOk === true) cam = "  cam=ok"
  else if (state.camMuted) cam = "  cam=muted"
  else if (state.camOk === false) cam = "  cam=black"
  return {
    clock: `QA ${elapsed}`,
    wall,
    line: `σ${acc} ${live} ${age} ${ok}  ${zone} ${d}  hdg${hdg}±${hdgAcc}  spd${spd} ${still}  vid=${vid}  size=${size}${cam}`,
  }
}

export function sampleFromState(state, t, iso) {
  return {
    t,
    iso,
    acc: finiteOrNull(state.acc),
    live: Boolean(state.live),
    age_ms: finiteOrNull(state.ageMs),
    silent_ms: finiteOrNull(state.silentMs),
    overlay_ok: Boolean(state.overlayOk),
    overlay_block: state.overlayBlock || null,
    zone: state.zone || null,
    d_m: finiteOrNull(state.dM),
    pin_id: state.pinId ?? null,
    dwell_ms: finiteOrNull(state.dwellMs),
    dwell_zone: state.dwellZone || null,
    overlay_ready: Boolean(state.overlayReady),
    hdg: finiteOrNull(state.hdg),
    hdg_acc: finiteOrNull(state.hdgAcc),
    pitch: finiteOrNull(state.pitch),
    spd: finiteOrNull(state.spd),
    still: Boolean(state.still),
    vid: state.vid || "none",
    size_m: finiteOrNull(state.sizeM),
    lat: finiteOrNull(state.lat),
    lng: finiteOrNull(state.lng),
    raw_lat: finiteOrNull(state.rawLat),
    raw_lng: finiteOrNull(state.rawLng),
    cam_ok: state.camOk == null ? null : Boolean(state.camOk),
    cam_live: state.camLive == null ? null : Boolean(state.camLive),
    cam_muted: state.camMuted == null ? null : Boolean(state.camMuted),
    cam_w: finiteOrNull(state.camW),
    cam_luma: finiteOrNull(state.camLuma),
  }
}

function finiteOrNull(n) {
  const x = Number(n)
  return Number.isFinite(x) ? x : null
}

export function createBuffer({now = () => Date.now(), hz = QA_HZ, maxSamples = QA_MAX_SAMPLES} = {}) {
  const minGap = 1000 / Math.max(hz, 0.1)
  let samples = []
  let marks = []
  let sentSamples = 0
  let sentMarks = 0
  let lastSampleAt = -Infinity
  let startedAt = null

  function recording() {
    return startedAt != null
  }

  function elapsed() {
    if (startedAt == null) return 0
    return Math.max(0, now() - startedAt)
  }

  function start(at = now()) {
    samples = []
    marks = []
    sentSamples = 0
    sentMarks = 0
    lastSampleAt = -Infinity
    startedAt = at
  }

  function stop() {
    startedAt = null
  }

  function pushSample(state) {
    if (!recording()) return null
    const tNow = now()
    if (tNow - lastSampleAt < minGap) return null
    lastSampleAt = tNow
    const sample = sampleFromState(state, tNow - startedAt, new Date(tNow).toISOString())
    samples.push(sample)
    if (samples.length > maxSamples) {
      const drop = samples.length - maxSamples
      samples = samples.slice(drop)
      sentSamples = Math.max(0, sentSamples - drop)
    }
    return sample
  }

  function pushRaw(sample) {
    if (!recording()) return null
    const tNow = now()
    if (tNow - lastSampleAt < minGap) return null
    lastSampleAt = tNow
    samples.push(sample)
    if (samples.length > maxSamples) {
      const drop = samples.length - maxSamples
      samples = samples.slice(drop)
      sentSamples = Math.max(0, sentSamples - drop)
    }
    return sample
  }

  function mark(kind) {
    if (!recording()) return null
    if (!qaMarkOk(kind)) return null
    const tNow = now()
    const row = {t: tNow - startedAt, iso: new Date(tNow).toISOString(), kind}
    marks.push(row)
    return row
  }

  function batchSince() {
    const nextSamples = samples.slice(sentSamples)
    const nextMarks = marks.slice(sentMarks)
    sentSamples = samples.length
    sentMarks = marks.length
    return {samples: nextSamples, marks: nextMarks}
  }

  function peek() {
    return {samples: samples.slice(), marks: marks.slice(), elapsedMs: elapsed(), recording: recording()}
  }

  return {start, stop, recording, elapsed, pushSample, pushRaw, mark, batchSince, peek}
}

/**
 * Stop must send the whole buffer. `batchSince` advances the sent cursor in
 * the same call as the slice, so a dropped `qa_batch` (mosaic navigate at
 * the 10 s flush — fountain walk 138) left `flush("stop")` with an empty
 * tail and the row stored shots with zero samples.
 */
export function payloadForFlush(buf, kind) {
  return kind === "stop" ? buf.peek() : buf.batchSince()
}

function viewerCall(viewer, name) {
  try {
    const fn = viewer?.[name]
    return typeof fn === "function" ? fn.call(viewer) : undefined
  } catch {
    return undefined
  }
}

export function viewerQaState(viewer) {
  const nearest = viewerCall(viewer, "nearestPin")
  const visible = viewerCall(viewer, "nearestDiscoverable")
  const pin = visible?.pin || nearest?.pin
  const zone = visible?.zone || nearest?.zone
  const dM = nearest?.rawHoriz
  const vid = viewer.modelLayer?.videoState?.()
  let vidKind = "none"
  if (vid?.kind === "mp4") {
    vidKind = vid.ended ? "ended" : vid.playing ? "play" : "pause"
  }
  let sizeM = null
  if (pin && zone && typeof viewer.overlaySize === "function") {
    sizeM = viewer.overlaySize(pin, zone, Math.max(Number(dM) || 0, 1))
  }
  const hasFix = viewer.myLat != null && viewer.accuracy != null
  const block = overlayBlock({
    speedLocked: Boolean(viewer._speedLocked),
    redacted: viewer.gpsRedacted?.() === true,
    hasFix,
    sampleOk: viewer.gpsSampleOk?.() === true,
  })
  // Why these three: a walk can hold the pin in frame for tens of seconds and
  // still never play (docs/reviews/20261021_qa_corpus_analysis.md §2). The
  // trace cannot say whether the dwell was never satisfied or satisfied and
  // lost, and it cannot say whether there was a poster to see. These three
  // fields close that gap on the next field session.
  // `_dwell.startedAt` is a `performance.now()` reading in ms, not a wall
  // clock; subtract the same origin or the number is meaningless.
  const dwell = pin ? viewer._dwell?.get(String(pin.id)) : null
  const dwellMs =
    dwell?.startedAt != null ? Math.max(0, performance.now() - dwell.startedAt) : null
  return {
    acc: viewer.accuracy,
    live: Boolean(viewer._liveGps),
    // Live age, not the receipt snapshot: `viewer.fixAgeMs/0` adds the time
    // since the last callback. Falls back to the raw field for a viewer stub.
    ageMs: typeof viewer.fixAgeMs === "function" ? viewer.fixAgeMs() : viewer._fixAgeMs,
    silentMs: viewer.geoSilentMs?.(),
    overlayOk: viewer.gpsOverlayOk?.() === true,
    overlayBlock: block,
    zone,
    dM,
    pinId: pin?.id ?? null,
    dwellMs,
    dwellZone: dwell ? zone : null,
    overlayReady: viewer.modelLayer?.overlayReady?.() === true,
    hdg: viewer.heading,
    hdgAcc: viewer._qaHeadingAcc,
    pitch: viewer.pitch,
    spd: viewer.speedMps,
    still: stillFromSpeed(viewer.speedMps),
    vid: vidKind,
    sizeM,
    lat: viewer.myLat,
    lng: viewer.myLng,
    rawLat: viewer._rawLat,
    rawLng: viewer._rawLng,
    ...(typeof viewer.cameraQaState === "function" ? viewer.cameraQaState() : {}),
  }
}

export function paintHudOverlay(ctx, root, canvasW, canvasH) {
  if (!ctx || !root || typeof document === "undefined") return
  const stage = root.querySelector("#ar-stage") || root
  const stageRect = stage.getBoundingClientRect()
  if (!(stageRect.width > 0) || !(stageRect.height > 0)) return

  for (const id of QA_SHOT_HUD_IDS) {
    const el = root.querySelector(`#${id}`)
    if (!el || el.hidden) continue
    const style = globalThis.getComputedStyle?.(el)
    if (style && (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0)) {
      continue
    }
    const text = (el.innerText || el.textContent || "").trim()
    if (!text) continue
    const box = canvasHudBox(el.getBoundingClientRect(), stageRect, canvasW, canvasH)
    const fontSize = Math.max(10, (style ? parseFloat(style.fontSize) : 16) * box.sy)
    const weight = style?.fontWeight || "700"
    const family = style?.fontFamily || "ui-monospace, monospace"
    const color = style?.color || "#4ade80"
    const align = style?.textAlign === "center" ? "center" : "left"
    ctx.save()
    ctx.font = `${weight} ${fontSize}px ${family}`
    ctx.fillStyle = color
    ctx.textBaseline = "top"
    ctx.textAlign = align
    ctx.shadowColor = "rgba(0,0,0,0.9)"
    ctx.shadowBlur = 6
    const x = align === "center" ? box.x + box.w / 2 : box.x
    const lineH = fontSize * 1.25
    text.split("\n").forEach((line, i) => {
      ctx.fillText(line, x, box.y + i * lineH, box.w || canvasW)
    })
    ctx.restore()
  }
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

function newClientId() {
  if (globalThis.crypto?.randomUUID) return crypto.randomUUID()
  return `qa-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/** Bind HUD + Record to an ARViewer instance. pose_filter is not imported. */
export function attachQa(viewer) {
  const root = viewer.el.querySelector("#ar-qa")
  const clockEl = viewer.el.querySelector("#ar-qa-clock")
  const wallEl = viewer.el.querySelector("#ar-qa-wall")
  const lineEl = viewer.el.querySelector("#ar-qa-hud")
  const recBtn = viewer.el.querySelector("#ar-qa-rec")
  const stopBtn = viewer.el.querySelector("#ar-qa-stop")
  const markRoot = viewer.el.querySelector("#ar-qa-marks")
  const buf = createBuffer()
  let clientId = null
  let lastFlushAt = 0
  let lastShotAt = 0
  let tickId = null
  const shots = createShotQueue({
    max: QA_SHOT_QUEUE,
    post: (item) => uploadShot(item),
  })

  function diagOn() {
    const ds = viewer.el?.dataset
    return ds?.arDiag === "true" || ds?.arQaAuto === "true" || ds?.arDiagnostics === "true"
  }

  function autoOn() {
    return diagOn()
  }

  function captureOn() {
    return diagOn()
  }

  function qaUrl(slugSeg, kind) {
    const q = captureOn() ? "?diag=true" : ""
    return `/e/${encodeURIComponent(slugSeg)}/qa/${kind}${q}`
  }

  function slug() {
    return viewer.pins?.[0]?.slug || ""
  }

  function eventId() {
    return viewer.pins?.[0]?.event_id
  }

  function paint() {
    if (!root) return
    const on = captureOn()
    root.hidden = !on
    if (!on) return
    const state = viewerQaState(viewer)
    const hud = formatHud({
      ...state,
      elapsedMs: buf.elapsed(),
      now: new Date(),
    })
    if (clockEl) clockEl.textContent = hud.clock
    if (wallEl) wallEl.textContent = hud.wall
    if (lineEl) lineEl.textContent = hud.line
    if (recBtn) recBtn.hidden = autoOn() || buf.recording()
    if (stopBtn) stopBtn.hidden = !buf.recording()
    if (markRoot) markRoot.hidden = !buf.recording()
  }

  function clearTick() {
    if (tickId) clearInterval(tickId)
    tickId = null
  }

  function flush(kind) {
    if (!clientId) return
    if (!buf.recording() && kind !== "stop") return
    const {samples, marks} = payloadForFlush(buf, kind)
    if (kind !== "stop" && samples.length === 0 && marks.length === 0) return
    const payload = {client_id: clientId, samples, marks}
    if (kind === "stop") {
      viewer.pushEvent("qa_stop", payload)
      keepaliveStop(payload)
    } else {
      viewer.pushEvent("qa_batch", payload)
    }
    lastFlushAt = Date.now()
  }

  function keepaliveStop(payload) {
    const s = slug()
    if (!s) return
    try {
      fetch(qaUrl(s, "stop"), {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": csrfToken(),
        },
        body: JSON.stringify(payload),
        keepalive: true,
      })
    } catch {
      // unload
    }
  }

  function tick() {
    if (!buf.recording()) return
    buf.pushSample(viewerQaState(viewer))
    if (Date.now() - lastFlushAt >= QA_BATCH_MS) flush("batch")
    if (Date.now() - lastShotAt >= QA_SHOT_MS) {
      lastShotAt = Date.now()
      captureShot("auto", buf.elapsed())
    }
    paint()
  }

  function start() {
    if (buf.recording() || !captureOn()) return
    clientId = newClientId()
    buf.start()
    lastFlushAt = Date.now()
    lastShotAt = 0
    viewer.pushEvent(
      "qa_start",
      {
        client_id: clientId,
        event_id: eventId(),
        user_agent: navigator.userAgent,
      },
      (reply) => {
        if (reply && reply.ok === false) {
          buf.stop()
          clearTick()
          clientId = null
          paint()
          if (lineEl) lineEl.textContent = `QA start failed: ${reply.error || "error"}`
        }
      }
    )
    clearTick()
    tickId = setInterval(tick, 500)
    paint()
  }

  function stop() {
    if (!buf.recording()) return
    buf.pushSample(viewerQaState(viewer))
    flush("stop")
    buf.stop()
    clearTick()
    paint()
  }

  function mark(kind) {
    const row = buf.mark(kind)
    if (!row) return
    flush("batch")
    captureShot(kind, row.t)
    paint()
  }

  function captureShot(kind, t) {
    if (!clientId) return
    const video = viewer.video
    const gl = viewer.glCanvas
    const overlay = viewer.canvas
    const w = Math.max(gl?.width || 0, overlay?.width || 0, video?.videoWidth || 0)
    const h = Math.max(gl?.height || 0, overlay?.height || 0, video?.videoHeight || 0)
    if (w < 8 || h < 8) return
    let dw = w
    let dh = h
    if (kind === "auto") {
      const edge = Math.max(w, h)
      if (edge > QA_SHOT_AUTO_MAX_EDGE) {
        const s = QA_SHOT_AUTO_MAX_EDGE / edge
        dw = Math.max(8, Math.round(w * s))
        dh = Math.max(8, Math.round(h * s))
      }
    }
    const off = document.createElement("canvas")
    off.width = dw
    off.height = dh
    const ctx = off.getContext("2d")
    if (!ctx) return
    try {
      if (video) ctx.drawImage(video, 0, 0, dw, dh)
      if (gl) ctx.drawImage(gl, 0, 0, dw, dh)
      if (overlay) ctx.drawImage(overlay, 0, 0, dw, dh)
      paintHudOverlay(ctx, viewer.el, dw, dh)
    } catch {
      return
    }
    off.toBlob(
      (blob) => {
        if (!blob) return
        shots.enqueue({
          kind,
          t,
          iso: new Date().toISOString(),
          blob,
          clientId,
          slug: slug(),
        })
      },
      "image/jpeg",
      kind === "auto" ? 0.45 : 0.55
    )
  }

  function uploadShot({kind, t, iso, blob, clientId: id, slug: s}) {
    if (!blob || !id || !s) return Promise.resolve()
    const body = new FormData()
    const name = shotFileName(t, iso)
    body.append("client_id", id)
    body.append("t", String(t))
    body.append("iso", iso || new Date().toISOString())
    body.append("kind", kind)
    body.append("shot", blob, name)
    return fetch(qaUrl(s, "shots"), {
      method: "POST",
      headers: {"x-csrf-token": csrfToken()},
      body,
    })
  }

  function onPageHide() {
    if (!buf.recording() || !clientId) return
    stop()
  }

  recBtn?.addEventListener("click", (e) => {
    e.preventDefault()
    start()
  })
  stopBtn?.addEventListener("click", (e) => {
    e.preventDefault()
    stop()
  })
  markRoot?.addEventListener("click", (e) => {
    const kind = e.target?.closest("[data-qa-mark]")?.getAttribute("data-qa-mark")
    if (kind) mark(kind)
  })

  paint()

  // Same contract as mosaic_qa.js: the Diag toggle patches data-ar-diag on
  // #ar-root without remounting the hook (phx-update="ignore"). autoStart
  // only runs from the Start tap, so a mid-session flip used to paint the
  // HUD at 00:00.0 and never call qa_start — the 2026-11-01 14:01 fountain
  // WhatsApp walk is missing from the corpus because of that.
  let wasOn = diagOn()
  const observer =
    typeof MutationObserver === "function" && viewer?.el
      ? new MutationObserver(() => {
          const on = diagOn()
          if (on === wasOn) return
          wasOn = on
          if (on) start()
          else stop()
          paint()
        })
      : null
  observer?.observe(viewer.el, {
    attributes: true,
    attributeFilter: ["data-ar-diag", "data-ar-diagnostics", "data-ar-qa-auto"],
  })

  return {
    paint,
    noteRaw(lat, lng) {
      viewer._rawLat = lat
      viewer._rawLng = lng
    },
    noteHeadingAcc(deg) {
      viewer._qaHeadingAcc = deg
    },
    onPageHide,
    autoStart() {
      if (autoOn()) start()
    },
    noteHandoff() {
      if (!buf.recording()) return
      captureShot("handoff", buf.elapsed())
    },
    teardown() {
      observer?.disconnect()
      if (buf.recording()) stop()
      clearTick()
    },
  }
}
